import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewEncapsulation, input, output } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { ReportingFieldInfo, DateModifier, AnswerType } from "./data.ingestion.service";

@TypeManagerDecorator("s25-ng-ingestion-field-selector")
@Component({
    selector: "s25-ng-ingestion-field-selector",
    template: `
        <div class="field-selector">
            <div class="field-dropdown">
                <label>Add Field:</label>
                <select
                    [(ngModel)]="selectedFieldForDropdown"
                    (ngModelChange)="onFieldSelected(selectedFieldForDropdown)"
                    [disabled]="availableFields().length === 0"
                >
                    <option [ngValue]="null">Select a field...</option>
                    @for (field of availableFields(); track field.reportingFieldCode) {
                        <option [ngValue]="field">
                            {{ field.reportingFieldCode }}
                        </option>
                    }
                </select>
            </div>

            @if (selectedFields().length > 0) {
                <div class="selected-fields">
                    <h5>Selected Fields ({{ selectedFields().length }}):</h5>
                    <div class="field-list">
                        @for (field of selectedFields(); track field.reportingFieldCode) {
                            <div class="field-item">
                                <span class="field-name">{{ field.reportingFieldCode }}</span>
                                @if (isDateField(field.reportingFieldCode) && mode() === "report") {
                                    <div class="date-modifier">
                                        <select
                                            [(ngModel)]="field.dateModifier"
                                            (ngModelChange)="
                                                onDateModifierChanged(field.reportingFieldCode, field.dateModifier)
                                            "
                                        >
                                            <option [ngValue]="null">No grouping</option>
                                            <option [ngValue]="DateModifier.YEAR">Year</option>
                                            <option [ngValue]="DateModifier.MONTH_YEAR">Month/Year</option>
                                            <option [ngValue]="DateModifier.DAY_MONTH_YEAR">Day/Month/Year</option>
                                            <option [ngValue]="DateModifier.DAY_OF_WEEK">Day of Week</option>
                                        </select>
                                    </div>
                                }
                                <button
                                    class="aw-button aw-button--danger-outline"
                                    (click)="removeField(field.reportingFieldCode)"
                                >
                                    ×
                                </button>
                            </div>
                        }
                    </div>
                </div>
            }

            <div class="actions">
                <button class="aw-button aw-button--outline" (click)="selectAll()">Select All</button>
                <button class="aw-button aw-button--outline" (click)="selectNone()">Select None</button>
            </div>
        </div>
    `,
    styles: `
        .field-selector {
            padding: 15px;
        }

        .field-dropdown {
            margin-bottom: 20px;
        }

        .field-dropdown label {
            display: block;
            margin-bottom: 5px;
            font-weight: 500;
        }

        .field-dropdown select {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #ced4da;
            border-radius: 4px;
            background: white;
        }

        .selected-fields {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            padding: 15px;
            margin-bottom: 20px;
        }

        .selected-fields h5 {
            margin: 0 0 10px 0;
            color: #495057;
        }

        .field-list {
            display: flex;
            flex-direction: column;
            gap: 10px;
        }

        .field-item {
            display: flex;
            align-items: center;
            background: white;
            border: 1px solid #ced4da;
            border-radius: 4px;
            padding: 10px;
        }

        .field-name {
            flex: 1;
            font-weight: 500;
            margin-right: 10px;
        }

        .date-modifier {
            margin-right: 10px;
        }

        .date-modifier select {
            padding: 4px 8px;
            border: 1px solid #ced4da;
            border-radius: 3px;
            background: white;
            font-size: 0.9em;
        }

        .btn {
            padding: 5px 10px;
            border: 1px solid #ddd;
            background: #f5f5f5;
            cursor: pointer;
            border-radius: 4px;
            margin-right: 5px;
        }

        .btn-sm {
            font-size: 0.875em;
            padding: 2px 6px;
        }

        .btn-secondary {
            background: #6c757d;
            color: white;
            border-color: #6c757d;
        }

        .btn-secondary:hover:not(:disabled) {
            background: #5a6268;
        }

        .btn-secondary:disabled {
            background: #6c757d;
            opacity: 0.6;
            cursor: not-allowed;
        }

        .btn-danger {
            color: #dc3545;
            background: transparent;
            border: none;
        }

        .btn-danger:hover {
            background: #dc3545;
            color: white;
        }

        .actions {
            display: flex;
            gap: 10px;
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionFieldSelectorComponent {
    availableFields = input<ReportingFieldInfo[]>([]);
    selectedFields = input<ReportingFieldInfo[]>([]);
    mode = input<"search" | "report">("search");

    fieldsChanged = output<ReportingFieldInfo[]>();

    selectedFieldForDropdown: ReportingFieldInfo | null = null;

    // Expose enums to template
    DateModifier = DateModifier;
    AnswerType = AnswerType;

    constructor(private cd: ChangeDetectorRef) {}

    onFieldSelected(field: ReportingFieldInfo | null) {
        if (!field) return;

        const currentFields = this.selectedFields();

        // Check if field is already selected
        if (currentFields.some((f) => f.reportingFieldCode === field.reportingFieldCode)) {
            setTimeout(() => {
                this.selectedFieldForDropdown = null; // Reset dropdown
                this.cd.detectChanges();
            }, 0);
            return;
        }

        // Add the whole ReportingFieldInfo object to selection
        const newField: ReportingFieldInfo = { ...field };
        const newFields = [...currentFields, newField];
        this.fieldsChanged.emit(newFields);

        // Reset dropdown after change detection cycle
        setTimeout(() => {
            this.selectedFieldForDropdown = null;
            this.cd.detectChanges();
        }, 0);
    }

    removeField(fieldCode: string) {
        const newFields = this.selectedFields().filter((f) => f.reportingFieldCode !== fieldCode);
        this.fieldsChanged.emit(newFields);
    }

    onDateModifierChanged(fieldCode: string, dateModifier: DateModifier | null) {
        const updatedFields = this.selectedFields().map((field) => {
            if (field.reportingFieldCode === fieldCode) {
                return { ...field, dateModifier: dateModifier || undefined };
            }
            return field;
        });

        this.fieldsChanged.emit(updatedFields);
    }

    isDateField(fieldCode: string): boolean {
        const field = this.availableFields().find((f) => f.reportingFieldCode === fieldCode);
        return field?.answerType === AnswerType.DATETIME;
    }

    selectAll() {
        const allFields: ReportingFieldInfo[] = this.availableFields().map((f) => ({ ...f }));
        this.fieldsChanged.emit(allFields);
    }

    selectNone() {
        this.fieldsChanged.emit([]);
    }
}
